// SETTINGS > WAREHOUSES (warehouse-master, 2026-10-01).
//
// The dispatch warehouses a quote is sent from. Every role sees the list;
// only an Admin can edit a warehouse, deactivate / reactivate it, or re-upload
// the CSV. ⚠️ THE SCREEN IS NOT THE GUARD: every write route refuses Sales and
// Viewer with a 403 whatever this renders, and the server owns every rule
// (models/warehouse.py). What the server says is shown verbatim.
//
// Nothing is ever deleted. An upload SAVES NOTHING until confirmed, and the
// confirm is all-or-nothing (the same pattern as Duty Rates).
//
// Contacts are internal: they are shown here, and never on a quote document.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getWarehouses, saveWarehouse, setWarehouseActive, previewWarehouseUpload, importWarehouseUpload,
  FTWZ_NOTE, type Warehouse, type WarehouseEdit, type WarehousePreview,
  api, DEFAULT_LOCATION_CODE_LABEL,
} from "@/services/api";
import { useIsAdminOrFalse } from "@/auth/AuthContext";
import { useNeedsSideways } from "@/lib/useNeedsSideways";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows } from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box", width: "100%",
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium,
};
const BOX: React.CSSProperties = {
  marginBottom: spacing[12], padding: spacing[12],
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.panelBg,
  display: "flex", flexDirection: "column", gap: spacing[8],
};
const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const ERROR: React.CSSProperties = { ...NOTE, color: semanticColors.destructive };
const TH: React.CSSProperties = {
  textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`,
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  fontWeight: typography.fontWeight.medium, borderBottom: `1px solid ${semanticColors.border}`,
  whiteSpace: "nowrap", position: "sticky", top: 0, backgroundColor: semanticColors.surface,
};
const TD: React.CSSProperties = {
  padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.base,
  borderBottom: `1px solid ${semanticColors.border}`, whiteSpace: "nowrap", verticalAlign: "top",
};
const PILL: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", height: heights.pill, padding: `0 ${spacing[8]}px`,
  borderRadius: radius.base, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
  border: "1px solid",
};
const TABLE_MIN = 980;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.onload = () => {
      const text = String(reader.result ?? "");
      resolve(text.slice(text.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

interface Form {
  business_tag: string; city: string; province_code: string; gstin: string;
  ship_from_address: string; bill_to_address: string; import_contact: string;
  local_contact: string; is_ftwz: boolean;
}

function formOf(w: Warehouse): Form {
  return {
    business_tag: w.business_tag ?? "", city: w.city ?? "", province_code: w.province_code ?? "",
    gstin: w.gstin ?? "", ship_from_address: w.ship_from_address ?? "",
    bill_to_address: w.bill_to_address ?? "", import_contact: w.import_contact ?? "",
    local_contact: w.local_contact ?? "", is_ftwz: w.is_ftwz,
  };
}

function StatusPill({ w }: { w: Warehouse }) {
  if (!w.active) {
    return <span style={{ ...PILL, color: semanticColors.textMuted, backgroundColor: semanticColors.panelBg, borderColor: semanticColors.border }}>Inactive</span>;
  }
  if (w.is_ftwz) {
    return <span title={FTWZ_NOTE} style={{ ...PILL, color: semanticColors.warningTextOnTint, backgroundColor: semanticColors.warningBg, borderColor: colors.amber600 }}>FTWZ</span>;
  }
  return <span style={{ ...PILL, color: semanticColors.success, backgroundColor: semanticColors.successBg, borderColor: semanticColors.successBorder }}>Active</span>;
}

export function WarehousesTab() {
  const isAdmin = useIsAdminOrFalse();
  const [rows, setRows] = useState<Warehouse[]>([]);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [done, setDone] = useState("");

  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [editError, setEditError] = useState("");
  const [busy, setBusy] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<{ name: string; b64: string } | null>(null);
  const [preview, setPreview] = useState<WarehousePreview | null>(null);
  const [uploadError, setUploadError] = useState("");
  // Configurable identity (P1-6): the sheet's location-code heading, from
  // Settings > Company. Read only when the upload panel opens -- the one
  // place it is shown -- so no other view makes an extra request.
  const [codeLabel, setCodeLabel] = useState(DEFAULT_LOCATION_CODE_LABEL);
  useEffect(() => {
    if (!uploadOpen) return;
    let cancelled = false;
    void api.getCompanySettingsFromBackend().then((r) => {
      if (!cancelled && r.ok && r.settings?.location_code_label) setCodeLabel(r.settings.location_code_label);
    });
    return () => { cancelled = true; };
  }, [uploadOpen]);

  const [tableRef, sideways] = useNeedsSideways(TABLE_MIN);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await getWarehouses();
    setLoading(false);
    if (!r.ok || !r.warehouses) { setLoadError(r.error ?? "The warehouses could not be read."); return; }
    setLoadError("");
    setRows(r.warehouses);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((w) => showInactive || w.active)
      .filter((w) => !q || [w.code, w.city, w.business_tag, w.gstin, w.state_name]
        .some((v) => (v ?? "").toLowerCase().includes(q)));
  }, [rows, search, showInactive]);

  const counts = useMemo(() => ({
    active: rows.filter((w) => w.active && !w.is_ftwz).length,
    ftwz: rows.filter((w) => w.active && w.is_ftwz).length,
    inactive: rows.filter((w) => !w.active).length,
  }), [rows]);

  function startEdit(w: Warehouse) {
    setDone(""); setEditError(""); setEditing(w); setForm(formOf(w));
  }

  async function saveEdit() {
    if (!editing || !form) return;
    setBusy(true); setEditError("");
    const before = formOf(editing);
    const edit: WarehouseEdit = {};
    (Object.keys(form) as (keyof Form)[]).forEach((k) => {
      if (form[k] !== before[k]) (edit as Record<string, unknown>)[k] = form[k];
    });
    const r = await saveWarehouse(editing.id, edit);
    setBusy(false);
    if (!r.ok) { setEditError(r.forbidden ? "Only an Admin can edit a warehouse." : (r.error ?? "Not saved.")); return; }
    setEditing(null); setForm(null);
    setDone(`Saved ${r.warehouse?.label}.`);
    void load();
  }

  async function toggleActive(w: Warehouse) {
    setDone(""); setLoadError("");
    const r = await setWarehouseActive(w.id, !w.active);
    if (!r.ok) { setLoadError(r.forbidden ? "Only an Admin can do that." : (r.error ?? "Not saved.")); return; }
    setDone(`${r.warehouse?.label} ${w.active ? "deactivated" : "reactivated"}.`);
    void load();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    setUploadError(""); setPreview(null); setFile(null);
    if (!f) return;
    try {
      const b64 = await fileToBase64(f);
      setFile({ name: f.name, b64 });
      const r = await previewWarehouseUpload(f.name, b64);
      if (!r.ok || !r.preview) { setUploadError(r.error ?? "The file could not be previewed."); return; }
      setPreview(r.preview);
    } catch (err) {
      setUploadError((err as Error).message);
    }
  }

  async function confirmUpload() {
    if (!file) return;
    setBusy(true); setUploadError("");
    const r = await importWarehouseUpload(file.name, file.b64);
    setBusy(false);
    if (!r.ok) { setUploadError(r.error ?? "Nothing was imported."); return; }
    const s = r.preview?.summary;
    setDone(s ? `Imported: ${s.new} new, ${s.update} updated, ${s.unchanged} unchanged.` : "Imported.");
    setPreview(null); setFile(null); setUploadOpen(false);
    void load();
  }

  const canConfirm = !!preview && preview.summary.error === 0 && (preview.summary.new + preview.summary.update) > 0 && !busy;

  return (
    <div data-testid="warehouses-tab" style={{ display: "flex", flexDirection: "column" }}>
      <div data-testid="warehouses-note" style={{ ...NOTE, marginBottom: spacing[12] }}>
        The warehouses a quote is dispatched from. A quote's GST uses its dispatch warehouse's
        State and GSTIN. FTWZ sites cannot be chosen yet ({FTWZ_NOTE.split("— ")[1]}).
        {isAdmin ? " Nothing is ever deleted: deactivate a warehouse instead."
          : " Only an Admin can change them."}
      </div>

      <div data-testid="warehouses-summary" className="flex" style={{ gap: spacing[12], flexWrap: "wrap", marginBottom: spacing[12] }}>
        {([["warehouses-count-active", "Can be chosen", counts.active],
           ["warehouses-count-ftwz", "FTWZ (not selectable)", counts.ftwz],
           ["warehouses-count-inactive", "Inactive", counts.inactive]] as const).map(([id, label, n]) => (
          <div key={id} data-testid={id} style={{
            flex: "1 1 160px", padding: spacing[12], border: `1px solid ${semanticColors.border}`,
            borderRadius: radius.base, backgroundColor: semanticColors.surface, boxShadow: shadows.sm,
          }}>
            <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>{label}</div>
            <div className="tabular-nums" style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold }}>{n}</div>
          </div>
        ))}
        {isAdmin && (
          <div className="flex items-center" style={{ gap: spacing[8] }}>
            <button data-testid="warehouses-upload-open" style={BUTTON}
                    onClick={() => { setUploadOpen((v) => !v); setPreview(null); setUploadError(""); }}>
              Upload CSV
            </button>
          </div>
        )}
      </div>

      {isAdmin && uploadOpen && (
        <div data-testid="warehouses-upload" style={BOX}>
          <div style={NOTE}>
            Columns: LOCATION, {codeLabel}, Bill to Address, Ship to Address, GST No., Import
            Shipment Contact, Local INR Shipment Contact, Province Code. A Loc# already listed is
            updated; a new one is added; nothing is removed. Nothing is saved until you confirm.
          </div>
          <input data-testid="warehouses-upload-file" type="file" accept=".csv" onChange={(e) => void onFile(e)} />
          {uploadError && <div data-testid="warehouses-upload-error" role="alert" style={ERROR}>{uploadError}</div>}
          {preview && (
            <div data-testid="warehouses-upload-preview" style={{ display: "flex", flexDirection: "column", gap: spacing[8] }}>
              <div data-testid="warehouses-upload-summary" style={NOTE}>
                {preview.summary.new} new · {preview.summary.update} changed · {preview.summary.unchanged} unchanged
                · {preview.summary.error} with errors
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
                  <thead><tr><th style={TH}>Row</th><th style={TH}>Warehouse</th><th style={TH}>Result</th><th style={TH}>Notes</th></tr></thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.index} data-testid={`warehouses-upload-row-${r.index}`} data-action={r.action}>
                        <td style={TD}>{r.index}</td>
                        <td style={TD}>{r.label ?? "—"}</td>
                        <td style={{ ...TD, color: r.action === "error" ? semanticColors.destructive : semanticColors.textPrimary }}>
                          {r.action === "error" ? r.error
                            : r.action === "update" ? `Changes: ${r.changes.join(", ")}`
                            : r.action === "new" ? "New" : "Unchanged"}
                        </td>
                        <td style={{ ...TD, whiteSpace: "normal", color: semanticColors.warningTextOnTint }}>{r.notes.join("; ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center" style={{ gap: spacing[8] }}>
                <button data-testid="warehouses-upload-confirm" style={PRIMARY} disabled={!canConfirm}
                        onClick={() => void confirmUpload()}>
                  {busy ? "Importing…" : "Confirm import"}
                </button>
                <button data-testid="warehouses-upload-cancel" style={BUTTON}
                        onClick={() => { setPreview(null); setFile(null); }}>Cancel</button>
                {preview.summary.error > 0 && (
                  <span data-testid="warehouses-upload-blocked" style={ERROR}>
                    Fix the rows with errors: the import is all or nothing.
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {done && <div data-testid="warehouses-done" style={{ ...NOTE, marginBottom: spacing[8] }}>{done}</div>}
      {loadError && <div data-testid="warehouses-error" role="alert" style={{ ...ERROR, marginBottom: spacing[8] }}>{loadError}</div>}

      <div className="flex items-center" data-testid="warehouses-filter-bar" style={{
        height: heights.filterBar, backgroundColor: semanticColors.panelBg, border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base, padding: `0 ${spacing[12]}px`, gap: spacing[12], marginBottom: spacing[8], flexWrap: "wrap",
      }}>
        <input data-testid="warehouses-search" placeholder="Search Loc#, city, tag, GSTIN" value={search}
               onChange={(e) => setSearch(e.target.value)} style={{ ...INPUT, width: layout.searchMinWidthWide, maxWidth: "100%" }} />
        <label style={{ ...NOTE, display: "flex", alignItems: "center", gap: spacing[4] }}>
          <input data-testid="warehouses-show-inactive" type="checkbox" checked={showInactive}
                 onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <span style={NOTE}><span data-testid="warehouses-shown" className="tabular-nums">{shown.length}</span> shown</span>
      </div>

      {loading && rows.length === 0 ? (
        <div data-testid="warehouses-loading" style={NOTE}>Loading warehouses…</div>
      ) : (
        <div ref={tableRef} data-testid="warehouses-scroll" data-sideways={sideways ? "true" : "false"}
             style={{ overflowX: sideways ? "auto" : "visible", border: `1px solid ${semanticColors.border}`,
                      borderRadius: radius.base, backgroundColor: semanticColors.surface }}>
          <table data-testid="warehouses-table" style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", minWidth: TABLE_MIN }}>
            <thead><tr>
              <th style={TH}>Loc#</th><th style={TH}>City</th><th style={TH}>Tag</th><th style={TH}>State</th>
              <th style={TH}>GSTIN</th><th style={TH}>Ship-from address</th><th style={TH}>Contacts</th>
              <th style={TH}>Status</th>{isAdmin && <th style={TH} />}
            </tr></thead>
            <tbody>
              {shown.map((w) => (
                <tr key={w.id} data-testid={`warehouse-row-${w.code}`} data-active={w.active ? "true" : "false"}
                    data-ftwz={w.is_ftwz ? "true" : "false"}>
                  <td style={{ ...TD, fontVariantNumeric: "tabular-nums", fontWeight: typography.fontWeight.medium }}>{w.code}</td>
                  <td style={TD}>{w.city}</td>
                  <td style={TD}>{w.business_tag ?? "—"}</td>
                  <td style={TD}>{w.state_name ?? "—"}{w.province_code ? ` (${w.province_code})` : ""}</td>
                  <td style={{ ...TD, fontVariantNumeric: "tabular-nums" }}>
                    {w.gstin ?? "—"}
                    {w.gstin_problem && (
                      <div data-testid={`warehouse-gstin-problem-${w.code}`}
                           style={{ fontSize: typography.fontSize.xs, color: semanticColors.warning, whiteSpace: "normal", maxWidth: 220 }}>
                        {w.gstin_problem}
                      </div>
                    )}
                  </td>
                  <td style={{ ...TD, whiteSpace: "normal", minWidth: 220, maxWidth: 320, fontSize: typography.fontSize.sm }}>
                    {w.ship_from_address ?? "—"}
                  </td>
                  <td style={{ ...TD, whiteSpace: "normal", minWidth: 180, fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
                    <div>Import: {w.import_contact || "—"}</div>
                    <div>Local: {w.local_contact || "—"}</div>
                  </td>
                  <td style={TD}><StatusPill w={w} /></td>
                  {isAdmin && (
                    <td style={TD}>
                      <div className="flex" style={{ gap: spacing[6] }}>
                        <button data-testid={`warehouse-edit-${w.code}`} style={BUTTON} onClick={() => startEdit(w)}>Edit</button>
                        <button data-testid={`warehouse-toggle-${w.code}`} style={BUTTON} onClick={() => void toggleActive(w)}>
                          {w.active ? "Deactivate" : "Reactivate"}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={isAdmin ? 9 : 8} data-testid="warehouses-empty" style={{ ...TD, color: semanticColors.textMuted }}>
                  {rows.length === 0 ? "No warehouses yet." : "No warehouse matches."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && editing && form && (
        <div data-testid="warehouse-edit-backdrop"
             onClick={(e) => { if (e.target === e.currentTarget && !busy) { setEditing(null); setForm(null); } }}
             style={{ position: "fixed", inset: 0, zIndex: 60, backgroundColor: semanticColors.overlay,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: spacing[16] }}>
          <div data-testid="warehouse-edit-dialog" role="dialog" aria-modal="true" aria-label="Edit warehouse"
               style={{ width: layout.modalWidth, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto",
                        backgroundColor: colors.white, borderRadius: radius.base, boxShadow: shadows.modal,
                        padding: spacing[20], display: "flex", flexDirection: "column", gap: spacing[10] }}>
            <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold }}>
              Edit {editing.label}
            </div>
            <div style={NOTE}>The Loc# ({editing.code}) cannot be changed.</div>
            {([
              ["city", "City"], ["business_tag", "Tag"], ["province_code", "Province code (e.g. MH)"],
              ["gstin", "GSTIN"], ["ship_from_address", "Ship-from address"], ["bill_to_address", "Bill-to address"],
              ["import_contact", "Import shipment contact"], ["local_contact", "Local INR shipment contact"],
            ] as const).map(([k, label]) => (
              <label key={k} style={{ ...NOTE, display: "flex", flexDirection: "column", gap: spacing[4] }}>
                {label}
                {k === "ship_from_address" || k === "bill_to_address" ? (
                  <textarea data-testid={`warehouse-field-${k}`} rows={2} value={form[k]}
                            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                            style={{ ...INPUT, height: "auto", resize: "vertical", padding: spacing[8] }} />
                ) : (
                  <input data-testid={`warehouse-field-${k}`} value={form[k]}
                         onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={INPUT} />
                )}
              </label>
            ))}
            <label style={{ ...NOTE, display: "flex", alignItems: "center", gap: spacing[6] }}>
              <input data-testid="warehouse-field-is_ftwz" type="checkbox" checked={form.is_ftwz}
                     onChange={(e) => setForm({ ...form, is_ftwz: e.target.checked })} />
              FTWZ (not selectable on a quote)
            </label>
            {editError && <div data-testid="warehouse-edit-error" role="alert" style={ERROR}>{editError}</div>}
            <div className="flex" style={{ justifyContent: "flex-end", gap: spacing[8] }}>
              <button data-testid="warehouse-edit-cancel" style={BUTTON} disabled={busy}
                      onClick={() => { setEditing(null); setForm(null); }}>Cancel</button>
              <button data-testid="warehouse-edit-save" style={PRIMARY} disabled={busy} onClick={() => void saveEdit()}>
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
